import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Minus, Loader2, ChevronLeft, ChevronRight, Cake, GlassWater, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useCart } from '../../lib/cart';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/Button';
import { ProductDetailModal } from './ProductDetailModal';
import { MenuItem, CartItemOption, MenuCategory } from '../../lib/types';
/**
 * Emilius Pizza (restaurant_id = 27) checkout upsell.
 *
 * Shows up before navigating to checkout and offers four upsell groups:
 *   • Extras de pizza  (Extra queso, Borde relleno) — ONLY when the cart
 *                       already has at least one pizza
 *   • Bebidas
 *   • Postres
 *   • Adicionales
 *
 * Implementation mirrors the Crack Pollo upsell modal exactly — same
 * quick-add flow, same ProductDetailModal fallback for customizable items.
 * It is intentionally a separate component so it doesn't change any
 * behavior for other restaurants.
 */
interface EmiliusCheckoutUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  restaurantId: string | number;
  restaurantName: string;
  restaurantSlug?: string;
}
type SectionKey = 'extras_pizza' | 'bebidas' | 'postres' | 'adicionales';
// ─── Pizza add-on pricing per size ────────────────────────────────
// Extra Queso and Borde Relleno are priced per pizza size, NOT a flat
// rate. The DB stores them with a single base price; we override here
// based on the size detected in each pizza's selected_options. This is
// the single source of truth — change it here and the modal updates.
const SIZE_PRICES: Record<string, number> = {
  '18': 2100,
  '18"': 2100,
  '18 pulgadas': 2100,
  super: 2000,
  grande: 1800,
  mediana: 1500,
  mediano: 1500,
  personal: 1300
};
function priceForSize(size: string | null): number {
  if (!size) return SIZE_PRICES.personal;
  const key = size.toLowerCase().trim();
  if (SIZE_PRICES[key] != null) return SIZE_PRICES[key];
  // Fuzzy contains match so things like "Pizza Grande" still resolve
  for (const k of Object.keys(SIZE_PRICES)) {
    if (key.includes(k)) return SIZE_PRICES[k];
  }
  return SIZE_PRICES.personal;
}
function detectSizeFromLine(line: any): string | null {
  if (!line?.selected_options || !Array.isArray(line.selected_options)) return null;
  // Look for an option whose group looks like "tamaño" / "size"
  const sizeOpt = line.selected_options.find((o: any) => {
    const g = (o?.option_group || '').toLowerCase();
    return g.includes('tamaño') || g.includes('tamano') || g.includes('size');
  }) || null;
  return sizeOpt?.option_name ?? null;
}
function isExtraQuesoName(n: string): boolean {
  return (n || '').toLowerCase().includes('extra queso');
}
function isBordeRellenoName(n: string): boolean {
  return (n || '').toLowerCase().includes('borde relleno');
}
// La Chiniadita NUNCA acepta Extra queso ni Borde relleno — el extra
// queso ya viene incluido en la receta y el borde no aplica. Cualquier
// línea cuyo nombre contenga "chiniadita" se omite del flujo de
// add-ons en el checkout.
function isChiniaditaLine(name: string): boolean {
  return (name || '').toLowerCase().includes('chiniadita');
}
interface SectionDef {
  key: SectionKey;
  label: string;
  // Returns true if the category name matches this section.
  matches: (name: string) => boolean;
  // If true, only render this section when cart has a pizza.
  requiresPizza: boolean;
}
// Each section accepts a tight, explicit list of category names so a
// category that merely *contains* the word ("Pizzas adicionales", etc.)
// can't leak the wrong products into the upsell.
const SECTIONS: SectionDef[] = [{
  key: 'extras_pizza',
  label: 'Extras de pizza',
  matches: n => n === 'extras de pizza',
  requiresPizza: true
}, {
  key: 'bebidas',
  label: 'Bebidas',
  matches: n => n === 'bebidas' || n === 'refrescos',
  requiresPizza: false
}, {
  key: 'postres',
  label: 'Postres',
  matches: n => n === 'postres',
  requiresPizza: false
}, {
  key: 'adicionales',
  label: 'Adicionales',
  matches: n => n === 'adicionales',
  requiresPizza: false
}];
function categoryMatchesPizza(name: string): boolean {
  const n = name.toLowerCase();
  // Anything pizza-related EXCEPT the "Extras de pizza" category itself.
  return n.includes('pizza') && !n.includes('extra');
}
function isBeverageCategoryName(name: string): boolean {
  const normalized = (name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  return normalized === 'bebidas' || normalized.includes('refresco') || normalized.includes('caliente') || normalized.includes('batido') || normalized.includes('milkshake') || normalized.includes('combinado') || normalized.includes('coctel') || normalized.includes('cerveza') || normalized.includes('gaseosa') || normalized.includes('tropical') || normalized.includes('licor');
}
function isBeverageCategory(category: MenuCategory | undefined, bebidasParentId: string | null): boolean {
  if (!category) return false;
  return bebidasParentId !== null && String(category.parent_category_id ?? '') === bebidasParentId || isBeverageCategoryName(category.name);
}
function beverageEmoji(categoryName: string): string {
  const name = categoryName.toLowerCase();
  if (name.includes('caliente')) return '☕';
  if (name.includes('milkshake') || name.includes('batido')) return '🥤';
  if (name.includes('coctel')) return '🍹';
  if (name.includes('licor')) return '🥃';
  if (name.includes('cerveza')) return '🍺';
  if (name.includes('gaseosa') || name.includes('tropical')) return '🫧';
  return '🥤';
}
export function EmiliusCheckoutUpsellModal({
  isOpen,
  onClose,
  onContinue,
  restaurantId,
  restaurantName,
  restaurantSlug
}: EmiliusCheckoutUpsellModalProps) {
  const {
    cart,
    addItem,
    updateQuantity
  } = useCart();
  const [allCategories, setAllCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [pizzaItemIds, setPizzaItemIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [productModalItem, setProductModalItem] = useState<MenuItem | null>(null);
  // ─── Flujo en dos fases ────────────────────────────────────────────
  // 1) 'extras'    → Extra queso / Borde relleno por pizza (sólo si
  //                  el carrito tiene pizza con add-ons válidos).
  // 2) 'categories'→ Selector de 3 cards: Postres / Bebidas / Adicional.
  // 3) 'detail'    → Items de la categoría que el cliente eligió.
  //
  // En pedidos sin pizza, saltamos 'extras' y arrancamos en 'categories'.
  type View = 'extras' | 'categories' | 'detail';
  type DetailKey = 'postres' | 'bebidas' | 'adicionales';
  const [view, setView] = useState<View>('categories');
  const [detailKey, setDetailKey] = useState<DetailKey | null>(null);
  const [activeBeverageCategoryId, setActiveBeverageCategoryId] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchUpsell = async () => {
      setLoading(true);
      try {
        const {
          data: cats,
          error: catErr
        } = await supabase.from('menu_categories').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).order('sort_order', {
          ascending: true
        });
        if (catErr) throw catErr;
        const catsSafe = cats ?? [];
        const bebidasParent = catsSafe.find(category => category.is_parent && isBeverageCategoryName(category.name) && !category.parent_category_id);
        const bebidasParentId = bebidasParent ? String(bebidasParent.id) : null;
        // Keep every active child of the Bebidas parent, with a name-based
        // fallback for older menu configurations without the hierarchy.
        const upsellCats = catsSafe.filter(category => {
          const name = (category.name ?? '').toLowerCase().trim();
          return SECTIONS.some(section => section.matches(name)) || isBeverageCategory(category, bebidasParentId);
        });
        // Pizza categories: used to detect if the cart contains a pizza.
        const pizzaCats = catsSafe.filter(c => categoryMatchesPizza(c.name ?? ''));
        const upsellCatIds = upsellCats.map(c => c.id);
        const pizzaCatIds = pizzaCats.map(c => c.id);
        // Combined fetch — upsell items + pizza items (for the detection).
        const allTargetCatIds = Array.from(new Set([...upsellCatIds, ...pizzaCatIds]));
        if (allTargetCatIds.length === 0) {
          if (!cancelled) {
            setAllCategories(catsSafe);
            setItems([]);
            setPizzaItemIds(new Set());
          }
          return;
        }
        const {
          data: itemsData,
          error: itemsErr
        } = await supabase.from('menu_items').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).in('category_id', allTargetCatIds).order('sort_order', {
          ascending: true
        });
        if (itemsErr) throw itemsErr;
        const safeItems = itemsData ?? [];
        const pizzaIds = new Set(safeItems.filter(it => pizzaCatIds.includes(it.category_id)).map(it => String(it.id)));
        // Keep only upsell items in `items` (we don't render pizzas).
        const upsellItems = safeItems.filter(it => upsellCatIds.includes(it.category_id));
        if (!cancelled) {
          setAllCategories(catsSafe);
          setItems(upsellItems);
          setPizzaItemIds(pizzaIds);
        }
      } catch (err) {
        console.error('[EmiliusCheckoutUpsellModal] fetch error:', err);
        if (!cancelled) {
          setAllCategories([]);
          setItems([]);
          setPizzaItemIds(new Set());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUpsell();
    return () => {
      cancelled = true;
    };
  }, [isOpen, restaurantId]);
  // "Armá tu propia pizza" is intentionally a synthetic cart line:
  // it has no menu_item_id so it can persist the selected half-and-half
  // flavors as one custom order item. Treat it as a pizza here so it
  // receives the same first upsell screen (Extra queso / Borde relleno)
  // as a traditional pizza before the Postres/Bebidas/Adicionales screen.
  const isArmaTuPizzaLine = (line: {
    item_name_snapshot?: string | null;
  }) => String(line.item_name_snapshot ?? '').trim().toLowerCase() === 'armá tu propia pizza';
  // Does the cart currently contain at least one traditional or custom pizza?
  const hasPizzaInCart = useMemo(() => cart.items.some(ci => pizzaItemIds.has(String(ci.menu_item_id)) || isArmaTuPizzaLine(ci)), [cart.items, pizzaItemIds]);
  const bebidasParentId = useMemo(() => {
    const parent = allCategories.find(category => category.is_parent && isBeverageCategoryName(category.name) && !category.parent_category_id);
    return parent ? String(parent.id) : null;
  }, [allCategories]);
  const beverageCategories = useMemo(() => allCategories.filter(category => !category.is_parent && isBeverageCategory(category, bebidasParentId)).sort((a, b) => a.sort_order - b.sort_order), [allCategories, bebidasParentId]);
  // Group items by section. Bebidas may contain a parent category with
  // many children, so its children all map into the same upsell section.
  const grouped = useMemo(() => {
    const map: Record<SectionKey, MenuItem[]> = {
      extras_pizza: [],
      bebidas: [],
      postres: [],
      adicionales: []
    };
    for (const item of items) {
      const category = allCategories.find(c => c.id === item.category_id);
      const name = (category?.name ?? '').toLowerCase().trim();
      const section = isBeverageCategory(category, bebidasParentId) ? SECTIONS.find(candidate => candidate.key === 'bebidas') : SECTIONS.find(candidate => candidate.matches(name));
      if (section) map[section.key].push(item);
    }
    return map;
  }, [items, allCategories, bebidasParentId]);
  const getCartLine = (item: MenuItem) => {
    return cart.items.find(ci => String(ci.menu_item_id) === String(item.id) && (!ci.selected_options || ci.selected_options.length === 0));
  };
  // Match the cart's restaurantId exactly to satisfy the strict equality
  // check in addItem (string vs number).
  const cartRestaurantId = cart.restaurantId ?? restaurantId;
  const handleQuickAdd = (item: MenuItem) => {
    if (item.is_customizable) {
      setProductModalItem(item);
      return;
    }
    const ok = addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: [],
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    if (!ok) {
      console.warn('[EmiliusCheckoutUpsellModal] addItem rejected', {
        cartRestaurantId,
        itemRestaurantId: item.restaurant_id
      });
    }
  };
  const handleProductModalAdd = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    setProductModalItem(null);
  };
  const handleIncrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (line) {
      updateQuantity(line.id, line.quantity + 1);
    } else {
      handleQuickAdd(item);
    }
  };
  const handleDecrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (!line) return;
    updateQuantity(line.id, Math.max(0, line.quantity - 1));
  };
  // ─── Pizza add-on (Extra queso / Borde relleno) per cart line ────
  // Templates: the DB rows for "Extra queso" and "Borde relleno" that
  // live under the "Extras de pizza" category. We reuse their
  // menu_item_id, name, image, station_code, etc. and only override the
  // price based on the linked pizza's size.
  const extraQuesoTemplate = useMemo(() => items.find(it => isExtraQuesoName(it.name)) || null, [items]);
  const bordeRellenoTemplate = useMemo(() => items.find(it => isBordeRellenoName(it.name)) || null, [items]);
  // All pizza lines currently in the cart (each item is a separate
  // upsell target). De-duped only by cart line id — two identical
  // pizzas in two separate lines each get their own add-on prompt.
  // Chiniadita queda EXCLUIDA — no acepta estos extras.
  const pizzaLinesInCart = useMemo(() => {
    return cart.items.filter(ci => (pizzaItemIds.has(String(ci.menu_item_id)) || isArmaTuPizzaLine(ci)) && !isChiniaditaLine(ci.item_name_snapshot));
  }, [cart.items, pizzaItemIds]);
  // ¿La fase de "Extras de pizza" tiene contenido válido?
  const hasExtrasPhase = pizzaLinesInCart.length > 0 && (extraQuesoTemplate !== null || bordeRellenoTemplate !== null);
  // Al abrir el modal (o cuando termina de cargar la data y sabemos si
  // hay pizzas), seteamos la vista inicial. Si el pedido tiene pizza
  // arrancamos en 'extras', si no, vamos directo al picker de categorías.
  useEffect(() => {
    if (!isOpen) return;
    if (loading) return;
    setDetailKey(null);
    setActiveBeverageCategoryId(null);
    setView(hasExtrasPhase ? 'extras' : 'categories');
    // Solo nos importa correr cuando isOpen pasa a true o cuando la
    // info de pizzas termina de cargarse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading, hasExtrasPhase]);
  // Has this pizza line already received this add-on? We look it up by
  // (menu_item_id, notes) — the notes "Para: <pizza name>" uniquely
  // ties the add-on back to its parent pizza line.
  const findAddonLine = (template: MenuItem | null, pizzaLine: any): any | null => {
    if (!template || !pizzaLine) return null;
    const tag = `Para: ${pizzaLine.item_name_snapshot}`;
    return cart.items.find(ci => String(ci.menu_item_id) === String(template.id) && (ci.notes || '').startsWith(tag)) || null;
  };
  const handleAddPizzaAddon = (template: MenuItem | null, pizzaLine: any, size: string | null, unitPrice: number) => {
    if (!template || !pizzaLine) return;
    const sizeLabel = size ? size.charAt(0).toUpperCase() + size.slice(1) : 'Personal';
    const notes = `Para: ${pizzaLine.item_name_snapshot}`;
    const ok = addItem({
      menu_item_id: template.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: template.name,
      unit_price_snapshot: unitPrice,
      quantity: 1,
      notes,
      image_url: template.image_url,
      selected_options: [{
        option_name: sizeLabel,
        option_group: 'Tamaño',
        price_delta: 0
      }],
      station_code: template.station_code || null
    }, restaurantName, restaurantSlug);
    if (!ok) {
      console.warn('[EmiliusCheckoutUpsellModal] addon addItem rejected', {
        template: template?.name,
        pizza: pizzaLine?.item_name_snapshot,
        size,
        unitPrice
      });
    }
  };
  const handleIncrementAddon = (line: any) => {
    if (!line) return;
    updateQuantity(line.id, line.quantity + 1);
  };
  const handleDecrementAddon = (line: any) => {
    if (!line) return;
    updateQuantity(line.id, Math.max(0, line.quantity - 1));
  };
  const renderPizzaAddonsSection = () => {
    if (!hasPizzaInCart) return null;
    if (!extraQuesoTemplate && !bordeRellenoTemplate) return null;
    if (pizzaLinesInCart.length === 0) return null;
    return <div key="extras_pizza" className="mb-5" data-id="element-2612" data-mp="3cx85q0tpy5lp">
        <h3 className="text-base font-bold text-gray-900 mb-1" data-id="element-2613" data-mp="3cvx0rxojert0">
          Extras para tus pizzas
        </h3>
        <p className="text-xs text-gray-500 mb-3" data-id="element-2614" data-mp="4ftu9v95v969">
          Sumá un extra a cada pizza. El precio depende del tamaño.
        </p>
        <div className="space-y-3" data-id="element-2615" data-mp="99y36k1zlr9z">
          {pizzaLinesInCart.map(pizzaLine => {
          const size = detectSizeFromLine(pizzaLine);
          const unitPrice = priceForSize(size);
          const sizeLabel = size ? size.charAt(0).toUpperCase() + size.slice(1) : '';
          return <div key={pizzaLine.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-id="element-2616" data-mp="1yoqn71foyviq">
                <div className="flex items-baseline justify-between gap-2 mb-2" data-id="element-2617" data-mp="1azxwbjdxfduz">
                  <div className="font-bold text-sm text-gray-900 truncate" data-id="element-2618" data-mp="161wc2v4rl8hx">
                    {pizzaLine.item_name_snapshot}
                  </div>
                  {sizeLabel && <div className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-2619" data-mp="1f080cqi5ru77">
                      {sizeLabel}
                    </div>}
                </div>
                <div className="space-y-2" data-id="element-2620" data-mp="2qfmyvyq0mu4f">
                  {[extraQuesoTemplate, bordeRellenoTemplate].map(template => {
                if (!template) return null;
                const existing = findAddonLine(template, pizzaLine);
                return <div key={template.id} className="flex items-center gap-3 p-2.5 bg-white border border-gray-100 rounded-lg" data-id="element-2621" data-mp="2q6t748iwp6co">
                          <div className="flex-1 min-w-0" data-id="element-2622" data-mp="2z43n276o5jnx">
                            <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2623" data-mp="2u7lfz0nv998f">
                              {template.name}
                            </div>
                            <div className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2624" data-mp="33wwp2nar6dwb">
                              {formatCurrency(unitPrice)}
                            </div>
                          </div>
                          {existing ? <div className="flex items-center gap-2 shrink-0" data-id="element-2625" data-mp="3dwdtl1e9qlqp">
                              <button type="button" onClick={() => handleDecrementAddon(existing)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2626" data-mp="es4edl87w14">
                                <Minus className="w-4 h-4" data-id="element-2627" data-mp="3dr2a7pyhtrwu" />
                              </button>
                              <span className="font-bold text-sm w-5 text-center" data-id="element-2628" data-mp="3ikwqskisxqrp">
                                {existing.quantity}
                              </span>
                              <button type="button" onClick={() => handleIncrementAddon(existing)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2629" data-mp="38sr1jijxpng7">
                                <Plus className="w-4 h-4" data-id="element-2630" data-mp="10kcfjkt2s1ok" />
                              </button>
                            </div> : <button type="button" onClick={() => handleAddPizzaAddon(template, pizzaLine, size, unitPrice)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2631" data-mp="rlqw7nfs4imq">
                              <Plus className="w-3.5 h-3.5" data-id="element-2632" data-mp="3v0mnx6uli1tb" />
                              Agregar
                            </button>}
                        </div>;
              })}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderSection = (section: SectionDef) => {
    // The pizza extras section has its own per-pizza, size-aware
    // renderer — skip the generic flow here.
    if (section.key === 'extras_pizza') return renderPizzaAddonsSection();
    if (section.requiresPizza && !hasPizzaInCart) return null;
    const sectionItems = grouped[section.key];
    if (!sectionItems || sectionItems.length === 0) return null;
    return <div key={section.key} className="mb-5" data-id="element-2633" data-mp="mf2bry25l5rv">
        <h3 className="text-base font-bold text-gray-900 mb-3" data-id="element-2634" data-mp="2j8nbiregtv1s">
          {section.label}
        </h3>
        <div className="space-y-2" data-id="element-2635" data-mp="2o7qjokpqshdz">
          {sectionItems.map(item => {
          const line = getCartLine(item);
          return <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl" data-id="element-2636" data-mp="ii4ynceu638y">
                <div className="flex-1 min-w-0" data-id="element-2637" data-mp="rjw6amsxsk5g">
                  <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2638" data-mp="rjmectoc02x4">
                    {item.name}
                  </div>
                  {item.description && <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" data-id="element-2639" data-mp="3v1o1sb6a7m8e">
                      {item.description}
                    </div>}
                  <div className="text-sm font-bold text-gray-900 mt-1" data-id="element-2640" data-mp="8l0chckzq7s5">
                    {formatCurrency(item.price)}
                  </div>
                </div>
                {line && !item.is_customizable ? <div className="flex items-center gap-2 shrink-0" data-id="element-2641" data-mp="3c9ljywhwlzwk">
                    <button type="button" onClick={() => handleDecrement(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2642" data-mp="37baaimvitrev">
                      <Minus className="w-4 h-4" data-id="element-2643" data-mp="1du4ifaflkd66" />
                    </button>
                    <span className="font-bold text-sm w-5 text-center" data-id="element-2644" data-mp="1ispjtd6l52w7">
                      {line.quantity}
                    </span>
                    <button type="button" onClick={() => handleIncrement(item)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2645" data-mp="26i0f4f56252y">
                      <Plus className="w-4 h-4" data-id="element-2646" data-mp="l3me663505r6" />
                    </button>
                  </div> : <button type="button" onClick={() => handleQuickAdd(item)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2647" data-mp="q4jyopbmdbvu">
                    <Plus className="w-3.5 h-3.5" data-id="element-2648" data-mp="3tblr3ry91iuu" />
                    Agregar
                  </button>}
              </div>;
        })}
        </div>
      </div>;
  };
  if (!isOpen) return null;
  // Compute which sections will actually render (used for the empty state).
  const visibleSections = SECTIONS.filter(s => {
    // Pizza extras have their own per-pizza renderer; visible whenever
    // the cart has a pizza AND at least one add-on template exists.
    if (s.key === 'extras_pizza') {
      return hasPizzaInCart && (extraQuesoTemplate !== null || bordeRellenoTemplate !== null) && pizzaLinesInCart.length > 0;
    }
    if (s.requiresPizza && !hasPizzaInCart) return false;
    return (grouped[s.key]?.length ?? 0) > 0;
  });
  return <>
      <AnimatePresence data-id="element-2649" data-mp="1g6yvewbyow3f">
        <motion.div key="emilius-upsell-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2650" data-mp="1g6gu3lafzron">
          <motion.div key="emilius-upsell-card" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 320
        }} onClick={e => e.stopPropagation()} className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden" data-id="element-2651" data-mp="x7kzw9yl3dat">
            {(() => {
            // ── Definiciones del picker ────────────────────────────
            const CARDS: Array<{
              key: DetailKey;
              label: string;
              hint: string;
              icon: React.ReactNode;
              tint: string;
              emoji: string;
            }> = [{
              key: 'postres',
              label: 'Postres',
              hint: 'Para el toque dulce',
              icon: <Cake className="w-6 h-6" data-id="element-2652" data-mp="8j5fayr2d5n" />,
              tint: 'from-rose-50 to-rose-100 text-rose-700 border-rose-200',
              emoji: '🍰'
            }, {
              key: 'bebidas',
              label: 'Bebidas',
              hint: 'Refrescá tu pedido',
              icon: <GlassWater className="w-6 h-6" data-id="element-2653" data-mp="1x3e1anycu0jk" />,
              tint: 'from-sky-50 to-sky-100 text-sky-700 border-sky-200',
              emoji: '🥤'
            }, {
              key: 'adicionales',
              label: 'Algún adicional',
              hint: 'Lo que sume al festín',
              icon: <Sparkles className="w-6 h-6" data-id="element-2654" data-mp="7s0nsl09cx0m" />,
              tint: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
              emoji: '✨'
            }];
            const detailSection = detailKey ? CARDS.find(c => c.key === detailKey) : null;
            const allDetailItems: MenuItem[] = detailKey ? grouped[detailKey] || [] : [];
            const detailItems = detailKey === 'bebidas' && activeBeverageCategoryId ? allDetailItems.filter(item => String(item.category_id) === activeBeverageCategoryId) : allDetailItems;
            const beverageFilters = beverageCategories.filter(category => allDetailItems.some(item => String(item.category_id) === String(category.id)));
            const availableCards = CARDS.filter(c => (grouped[c.key]?.length ?? 0) > 0);
            // ── Header dinámico ─────────────────────────────────────
            const headerTitle = view === 'extras' ? '¿Querés un extra para tus pizzas?' : view === 'detail' && detailSection ? detailSection.label : 'Agregá una de estas delicias a tu orden';
            const headerSubtitle = view === 'extras' ? 'Sumá un toque más antes de seguir.' : view === 'detail' ? 'Tocá un producto para sumarlo al pedido.' : 'Una cosita más antes de pagar — vos elegís.';
            return <>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 border-b border-gray-100 shrink-0" data-id="element-2655" data-mp="1bxk47yjav94n">
                    <div className="min-w-0 flex items-start gap-2" data-id="element-2656" data-mp="120p6viyrjery">
                      {view === 'detail' && <button type="button" onClick={() => {
                    setView('categories');
                    setDetailKey(null);
                    setActiveBeverageCategoryId(null);
                  }} className="shrink-0 -ml-1 -mt-0.5 p-1.5 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" aria-label="Volver" data-id="element-2657" data-mp="1zoz9o06di6k4">
                          <ChevronLeft className="w-5 h-5" data-id="element-2658" data-mp="9aysr9x4scvu" />
                        </button>}
                      <div className="min-w-0" data-id="element-2659" data-mp="2xigolq6spmgj">
                        <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-2660" data-mp="31jacijk8n19f">
                          {headerTitle}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5" data-id="element-2661" data-mp="2njj5j2s52l4x">
                          {headerSubtitle}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={onClose} className="shrink-0 p-2 -mr-2 -mt-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-50" aria-label="Cerrar" data-id="element-2662" data-mp="2dses47i438c7">
                      <X className="w-5 h-5" data-id="element-2663" data-mp="29t5u2qg1goau" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2664" data-mp="1uxg7or6xy95l">
                    {loading ? <div className="flex items-center justify-center py-12" data-id="element-2665" data-mp="1zw1bf0lejays">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-id="element-2666" data-mp="1g8oiy7jd26vj" />
                      </div> : <AnimatePresence mode="wait" data-id="element-2667" data-mp="1q6b59f2zp0pj">
                        {/* ── Fase 1: Extras de pizza ──────────────── */}
                        {view === 'extras' && <motion.div key="view-extras" initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2668" data-mp="1l9ue0c0hu688">
                            {renderPizzaAddonsSection()}
                          </motion.div>}
                        {/* ── Fase 2: Picker de categorías ─────────── */}
                        {view === 'categories' && <motion.div key="view-categories" initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2669" data-mp="pyxmoqcpe6t1">
                            {availableCards.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-2670" data-mp="334ktyxk0ccq5">
                                No hay extras disponibles ahora.
                              </p> : <div className="space-y-2.5" data-id="element-2671" data-mp="2td7xfsn9kyds">
                                {availableCards.map(card => {
                        const count = grouped[card.key]?.length ?? 0;
                        return <button key={card.key} type="button" onClick={() => {
                          setDetailKey(card.key);
                          setActiveBeverageCategoryId(null);
                          setView('detail');
                        }} className={`group relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br ${card.tint} p-4 text-left transition-all hover:shadow-md hover:-translate-y-0.5 active:translate-y-0`} data-id="element-2672" data-mp="2ff9wboxyeyg8">
                                      <div className="flex items-center gap-4" data-id="element-2673" data-mp="1o92oesnmyl52">
                                        <div className="shrink-0 w-12 h-12 rounded-xl bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm" data-id="element-2674" data-mp="1t6kxqk5kf2dl">
                                          <span className="text-2xl" aria-hidden="true" data-id="element-2675" data-mp="2guuhlgj7xg23">
                                            {card.emoji}
                                          </span>
                                        </div>
                                        <div className="flex-1 min-w-0" data-id="element-2676" data-mp="vhssgokp9052">
                                          <div className="font-black text-base leading-tight" data-id="element-2677" data-mp="10ip4xqj49um3">
                                            {card.label}
                                          </div>
                                          <div className="text-xs opacity-80 mt-0.5" data-id="element-2678" data-mp="39rwqynk4ubnb">
                                            {card.hint}
                                            <span className="mx-1.5 opacity-50" data-id="element-2679" data-mp="3nosa58u3ljh3">
                                              ·
                                            </span>
                                            {count}{' '}
                                            {count === 1 ? 'opción' : 'opciones'}
                                          </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 shrink-0 opacity-60 group-hover:translate-x-0.5 transition-transform" data-id="element-2680" data-mp="2zz7f3rko6vrg" />
                                      </div>
                                    </button>;
                      })}
                              </div>}
                          </motion.div>}
                        {/* ── Fase 2-detail: Items de la categoría ─── */}
                        {view === 'detail' && detailSection && <motion.div key={`view-detail-${detailKey}`} initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2681" data-mp="3b5heylb96lx5">
                            {detailKey === 'bebidas' && beverageFilters.length > 0 && <div className="mb-4 rounded-2xl border border-sky-100 bg-sky-50/60 p-3" data-id="element-2682" data-mp="cri5yxzos80m">
                                  <div className="flex items-center justify-between gap-3 px-1 mb-2.5" data-id="element-2683" data-mp="7u04fso00v2n">
                                    <p className="text-xs font-bold uppercase tracking-wider text-sky-800" data-id="element-2684" data-mp="3u7x1jaccuyv">
                                      Elegí tu antojo
                                    </p>
                                    <span className="text-xs font-semibold text-sky-700" data-id="element-2685" data-mp="vldwrym5gqnm">
                                      {detailItems.length} opciones
                                    </span>
                                  </div>
                                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Tipos de bebidas" data-id="element-2686" data-mp="1j9ezgqwndcv4">
                                    <button type="button" role="tab" aria-selected={activeBeverageCategoryId === null} onClick={() => setActiveBeverageCategoryId(null)} className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold transition-colors ${activeBeverageCategoryId === null ? 'bg-sky-700 text-white shadow-sm' : 'bg-white text-sky-900 border border-sky-100 hover:border-sky-300'}`} data-id="element-2687" data-mp="34xdldt9yhc1j">
                                      ✦ Todas
                                    </button>
                                    {beverageFilters.map(category => {
                          const isActive = activeBeverageCategoryId === String(category.id);
                          return <button key={category.id} type="button" role="tab" aria-selected={isActive} onClick={() => setActiveBeverageCategoryId(String(category.id))} className={`shrink-0 rounded-full px-3 py-2 text-xs font-bold transition-colors ${isActive ? 'bg-sky-700 text-white shadow-sm' : 'bg-white text-sky-900 border border-sky-100 hover:border-sky-300'}`} data-id="element-2688" data-mp="272t511el4bay">
                                          {beverageEmoji(category.name)}{' '}
                                          {category.name}
                                        </button>;
                        })}
                                  </div>
                                </div>}
                            {detailItems.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-2689" data-mp="3udnkau78oc13">
                                No hay opciones de{' '}
                                {detailSection.label.toLowerCase()} disponibles
                                ahora.
                              </p> : <div className="space-y-2" data-id="element-2690" data-mp="29pl4vnu4z6lz">
                                {detailItems.map(item => {
                        const line = getCartLine(item);
                        return <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl" data-id="element-2691" data-mp="2nnrsmb5dfq7x">
                                      {item.image_url && <img src={item.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0 bg-white" data-id="element-2692" data-mp="2jlxx0ha19cem" />}
                                      <div className="flex-1 min-w-0" data-id="element-2693" data-mp="1ni8uw8n2m4lu">
                                        <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2694" data-mp="1ippez50wuxdv">
                                          {item.name}
                                        </div>
                                        {item.description && <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" data-id="element-2695" data-mp="26fjr1kdeohx2">
                                            {item.description}
                                          </div>}
                                        <div className="text-sm font-bold text-gray-900 mt-1" data-id="element-2696" data-mp="1xdaj929iv2nd">
                                          {formatCurrency(item.price)}
                                        </div>
                                      </div>
                                      {line && !item.is_customizable ? <div className="flex items-center gap-2 shrink-0" data-id="element-2697" data-mp="h2ygr1j8sjk4">
                                          <button type="button" onClick={() => handleDecrement(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2698" data-mp="2slun5v8sbzi4">
                                            <Minus className="w-4 h-4" data-id="element-2699" data-mp="3ldxqn2ii8e1b" />
                                          </button>
                                          <span className="font-bold text-sm w-5 text-center" data-id="element-2700" data-mp="3pc3w877lwnkn">
                                            {line.quantity}
                                          </span>
                                          <button type="button" onClick={() => handleIncrement(item)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2701" data-mp="1v29qim3aaooi">
                                            <Plus className="w-4 h-4" data-id="element-2702" data-mp="3calupzyk7ggz" />
                                          </button>
                                        </div> : <button type="button" onClick={() => handleQuickAdd(item)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2703" data-mp="3m2rwu2iize9w">
                                          <Plus className="w-3.5 h-3.5" data-id="element-2704" data-mp="9hcomsx5m6gy" />
                                          Agregar
                                        </button>}
                                    </div>;
                      })}
                              </div>}
                          </motion.div>}
                      </AnimatePresence>}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-gray-100 bg-white shrink-0 space-y-2" data-id="element-2705" data-mp="3qvhxpfbwwpt3">
                    {view === 'extras' ? <Button onClick={() => setView('categories')} className="w-full h-12 text-base font-bold" data-id="element-2706" data-mp="3hx5wkjdf69me">
                        Siguiente
                      </Button> : <>
                        <Button onClick={onContinue} className="w-full h-12 text-base font-bold" data-id="element-2707" data-mp="3nfyjg2bavn4d">
                          Continuar al checkout
                        </Button>
                        <button type="button" onClick={onContinue} className="w-full h-11 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-2708" data-mp="3ehuwd1ko2iag">
                          No gracias, continuar
                        </button>
                      </>}
                  </div>
                </>;
          })()}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {productModalItem && <ProductDetailModal isOpen={!!productModalItem} onClose={() => setProductModalItem(null)} item={productModalItem} onAdd={handleProductModalAdd} data-id="element-2709" data-mp="167n1dhvq207n" />}
    </>;
}